import React, { useEffect, useRef } from 'react';
import { Check, X, AlertTriangle, Info } from 'lucide-react';
import { toastDuration } from '../hooks/useToast';
import './Toast.css';

const ICONS = {
  success: Check,
  warning: AlertTriangle,
  info: Info,
  error: X,
};

const LABELS = {
  success: 'Success',
  warning: 'Warning',
  info: 'Info',
  error: 'Error',
};

// One toast with its own dismiss timer. The timer restarts when `id` changes, so the
// same text shown twice still gets its full time on screen.
const ToastItem = ({ id, message, type, duration, onClose }) => {
  // Read the latest onClose via a ref so the auto-dismiss timer is NOT reset every time the
  // parent re-renders with a new inline onClose identity (which could keep a toast on screen
  // indefinitely under frequent realtime re-renders).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!duration) return undefined;
    const timer = setTimeout(() => onCloseRef.current?.(), duration);
    return () => clearTimeout(timer);
  }, [id, message, duration]);

  const kind = ICONS[type] ? type : 'error';
  const Icon = ICONS[kind];
  const urgent = kind === 'error' || kind === 'warning';

  return (
    <div
      className={`toast toast-${kind}`}
      role={urgent ? 'alert' : 'status'}
      aria-live={urgent ? 'assertive' : 'polite'}
      aria-atomic="true"
    >
      <div className="toast-icon" aria-hidden="true"><Icon size={16} strokeWidth={2.5} /></div>
      <div className="toast-message">
        <span className="sr-only">{LABELS[kind]}: </span>
        {message}
      </div>
      <button type="button" className="toast-close" onClick={() => onClose?.()} aria-label="Dismiss notification">
        <X size={20} />
      </button>
    </div>
  );
};

// Toast notifications.
//
// Stack (preferred, with the useToast hook):  <Toast {...toastProps} />
//   toasts     [{ id, message, type, duration }] newest first
//   onDismiss  (id) => void
//
// Single (legacy):  <Toast message type onClose duration? id? position? />
//   Pass a new `id` per call to re-animate a repeated message. Errors ignore
//   `duration` and stay until dismissed.
//
// Desktop shows toasts top-right (or `position`); at 768px and below they sit at
// the bottom, above the tab bar and any module bottom bar (--toast-bottom-offset).
const Toast = ({ toasts, onDismiss, message, type = 'error', onClose, duration, id, position = 'top-right' }) => {
  let items;
  if (toasts) {
    items = toasts.map((t) => ({ ...t, onClose: () => onDismiss?.(t.id) }));
  } else if (message) {
    items = [{
      id: id ?? message,
      message,
      type,
      duration: type === 'error' ? null : toastDuration(type, duration),
      onClose,
    }];
  } else {
    items = [];
  }

  // The container stays mounted (even when empty) so screen readers track it
  return (
    <div className={`toast-stack toast-${position}`}>
      {items.map((t) => (
        <ToastItem key={t.id} {...t} />
      ))}
    </div>
  );
};

export default Toast;
